import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  Box,
  Typography,
  Button,
  IconButton,
  Divider,
  CircularProgress,
  Chip,
  Avatar,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PersonAddAlt1OutlinedIcon from "@mui/icons-material/PersonAddAlt1Outlined";
import DirectionsCarOutlinedIcon from "@mui/icons-material/DirectionsCarOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import apiClient from "../Utils/apiClient";

const getInitials = (name) => {
  if (!name) return "DR";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "DR";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const formatTimeLabel = (timestamp) => {
  const date = timestamp ? new Date(timestamp) : new Date();
  if (isNaN(date.getTime())) return "Just now";
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

const NewDriverRegistrationModal = ({ open, onClose, driverData, onApproved }) => {
  const navigate = useNavigate();
  const [approving, setApproving] = useState(false);

  if (!open) return null;

  const payload = driverData || {};
  const driverId = payload?.driverId || payload?._id || payload?.id;

  const driverName =
    (payload?.driverName && String(payload.driverName).trim()) ||
    `${payload?.firstName || ""} ${payload?.surName || ""}`.trim() ||
    "New Driver";

  const initials = getInitials(driverName);
  const phone = payload?.phoneNumber || payload?.phone || "N/A";
  const email = payload?.email || "N/A";
  const regNo = payload?.regNo || payload?.plateNumber || payload?.vehiclePlate || "N/A";
  const vehicleType = payload?.vehicleType || "Vehicle";
  const vehicleSeats = payload?.vehicleSeats ? `${payload.vehicleSeats} Seats` : null;
  const isCargo = Boolean(payload?.cargoEscort);
  const timeDisplay = formatTimeLabel(payload?.createdAt || payload?.timestamp);
  const vehicleImg = payload?.imgVehicle || null;

  const handleApprove = async () => {
    if (!driverId) {
      toast.error("Driver ID not found");
      return;
    }

    setApproving(true);
    try {
      await apiClient.put(`/driver/approve/${driverId}`, { isApproved: true });
      toast.success(`Driver ${driverName} approved and verified successfully!`, {
        position: toast.POSITION.TOP_CENTER,
      });

      // Dispatch event to refresh Drivers list
      try {
        window.dispatchEvent(new CustomEvent("refreshDriversTable"));
      } catch (_) {}

      if (onApproved) onApproved(driverId);
      onClose();
    } catch (err) {
      console.error("Failed to approve driver:", err);
      toast.error(err?.response?.data?.message || err?.message || "Failed to verify driver");
    } finally {
      setApproving(false);
    }
  };

  const handleViewDrivers = () => {
    onClose();
    navigate("/drivers");
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderRadius: "20px",
          maxWidth: "500px",
          width: "100%",
          p: { xs: 2.5, sm: 3 },
          boxShadow: "0 20px 45px rgba(0, 0, 0, 0.18)",
          overflow: "hidden",
          backgroundColor: "#FFFFFF",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: 2.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.75 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: "50%",
              bgcolor: "#EEF2FF",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <PersonAddAlt1OutlinedIcon sx={{ color: "#4F46E5", fontSize: 24 }} />
          </Box>
          <Box>
            <Typography
              sx={{
                fontSize: "17px",
                fontWeight: 700,
                color: "#111827",
                lineHeight: 1.25,
              }}
            >
              New Driver Registered
            </Typography>
            <Typography
              sx={{
                fontSize: "13px",
                color: "#6B7280",
                mt: 0.3,
              }}
            >
              Registered: {timeDisplay}
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: "#111827",
            p: 0.5,
            "&:hover": { bgcolor: "#F3F4F6" },
          }}
        >
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>

      {/* Driver Card */}
      <Box
        sx={{
          bgcolor: "#F8FAFC",
          borderRadius: "14px",
          p: 2,
          display: "flex",
          alignItems: "center",
          gap: 2,
          mb: 2.5,
          border: "1px solid #F1F5F9",
        }}
      >
        {vehicleImg ? (
          <Avatar
            src={vehicleImg}
            alt={driverName}
            sx={{
              width: 50,
              height: 50,
              borderRadius: "12px",
              boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
            }}
          />
        ) : (
          <Box
            sx={{
              width: 50,
              height: 50,
              borderRadius: "12px",
              bgcolor: "#DBEAFE",
              color: "#1D4ED8",
              fontWeight: 700,
              fontSize: "17px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {initials}
          </Box>
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Typography
              sx={{
                fontSize: "15px",
                fontWeight: 700,
                color: "#0F172A",
                lineHeight: 1.25,
              }}
              noWrap
            >
              {driverName}
            </Typography>
            <Chip
              label="Pending Verification"
              size="small"
              sx={{
                bgcolor: "#FEF3C7",
                color: "#92400e",
                fontWeight: 600,
                fontSize: "11px",
                height: "22px",
              }}
            />
          </Box>
          <Typography
            sx={{
              fontSize: "13px",
              color: "#64748B",
              mt: 0.3,
            }}
            noWrap
          >
            {phone} • {email}
          </Typography>
        </Box>
      </Box>

      {/* Vehicle & Registration Details */}
      <Box sx={{ mb: 2.5 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            py: 0.8,
          }}
        >
          <Typography sx={{ fontSize: "14px", color: "#64748B" }}>
            Plate / Registration No
          </Typography>
          <Typography sx={{ fontSize: "14px", fontWeight: 700, color: "#0F172A" }}>
            {regNo}
          </Typography>
        </Box>

        <Divider sx={{ borderColor: "#F1F5F9", my: 0.5 }} />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            py: 0.8,
          }}
        >
          <Typography sx={{ fontSize: "14px", color: "#64748B" }}>
            Vehicle Type & Capacity
          </Typography>
          <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#0F172A" }}>
            {vehicleType} {vehicleSeats ? `• ${vehicleSeats}` : ""}
          </Typography>
        </Box>

        <Divider sx={{ borderColor: "#F1F5F9", my: 0.5 }} />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            py: 0.8,
          }}
        >
          <Typography sx={{ fontSize: "14px", color: "#64748B" }}>
            Registration Category
          </Typography>
          <Chip
            icon={<DirectionsCarOutlinedIcon style={{ fontSize: 16 }} />}
            label={isCargo ? "Cargo Escort Driver" : "Passenger Escort Driver"}
            size="small"
            sx={{
              bgcolor: isCargo ? "#F3E8FF" : "#EFF6FF",
              color: isCargo ? "#7E22CE" : "#1D4ED8",
              fontWeight: 600,
              fontSize: "12px",
            }}
          />
        </Box>
      </Box>

      {/* Instruction Note */}
      <Box
        sx={{
          bgcolor: "#F0FDF4",
          border: "1px solid #DCFCE7",
          borderRadius: "10px",
          px: 1.75,
          py: 1.4,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          mb: 2.5,
        }}
      >
        <InfoOutlinedIcon sx={{ color: "#166534", fontSize: 20, flexShrink: 0 }} />
        <Typography
          sx={{
            fontSize: "13px",
            fontWeight: 500,
            color: "#166534",
            lineHeight: 1.35,
          }}
        >
          Review the vehicle and driver details. Verify now to activate this driver account.
        </Typography>
      </Box>

      {/* Action Buttons */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
        <Button
          fullWidth
          variant="contained"
          onClick={handleApprove}
          disabled={approving}
          startIcon={
            approving ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <CheckCircleOutlineIcon sx={{ fontSize: 20 }} />
            )
          }
          sx={{
            bgcolor: "#10B981",
            color: "#FFFFFF",
            py: 1.35,
            borderRadius: "10px",
            fontSize: "15px",
            fontWeight: 600,
            textTransform: "none",
            boxShadow: "none",
            "&:hover": {
              bgcolor: "#059669",
              boxShadow: "none",
            },
          }}
        >
          {approving ? "Verifying Driver..." : "Verify & Approve Driver"}
        </Button>

        <Button
          fullWidth
          variant="outlined"
          onClick={handleViewDrivers}
          startIcon={<OpenInNewIcon sx={{ fontSize: 18 }} />}
          sx={{
            color: "#374151",
            borderColor: "#D1D5DB",
            py: 1.2,
            borderRadius: "10px",
            fontSize: "14px",
            fontWeight: 600,
            textTransform: "none",
            "&:hover": {
              borderColor: "#9CA3AF",
              bgcolor: "#F9FAFB",
            },
          }}
        >
          Open Drivers Management
        </Button>
      </Box>
    </Dialog>
  );
};

NewDriverRegistrationModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  driverData: PropTypes.object,
  onApproved: PropTypes.func,
};

export default NewDriverRegistrationModal;
